import { useCallback, useEffect, useRef, useState } from "react";
import { subscribeMicGate } from "../speech/micGate";

// After the app finishes speaking, wait this long before resuming the mic: the
// speaker is still draining the tail of the last word when the utterance's
// `end` event fires, and the recognizer needs a moment to actually re-capture.
const RESUME_TAIL_MS = 300;

// Is the browser producing speech right now? Used as a last-line backstop when
// deciding whether to act on a recognition result (see onresult below), so the
// app never treats its own read-aloud voice as a command even if the engine
// keeps capturing through it.
function isSpeaking(): boolean {
  return (
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    window.speechSynthesis.speaking
  );
}

// The Web Speech API isn't in TypeScript's DOM lib, so we declare the minimal
// shape we use. Names are prefixed to avoid clashing with any future lib types.
interface SpeechRecAlternative {
  transcript: string;
}
interface SpeechRecResult extends ArrayLike<SpeechRecAlternative> {
  isFinal: boolean;
}
interface SpeechRecEvent {
  results: ArrayLike<SpeechRecResult>;
}
interface SpeechRecErrorEvent {
  error: string;
}
interface SpeechRec {
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: SpeechRecErrorEvent) => void) | null;
  onstart: (() => void) | null;
  onaudiostart: (() => void) | null;
}
type SpeechRecCtor = new () => SpeechRec;

function getCtor(): SpeechRecCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecCtor;
    webkitSpeechRecognition?: SpeechRecCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * Wraps the browser's continuous speech recognition. Calls `onResult` with the
 * live transcript as the user speaks (interim results) so commands fire as soon
 * as the word is recognized, rather than waiting for an end-of-speech pause.
 * `isFinal` marks the recognizer's finalized transcript for that utterance.
 * Returns controls + state for a mic toggle.
 *
 * Recognition is restarted automatically on `onend` (browsers stop after a
 * pause), so it keeps listening until the user turns it off.
 */
export function useSpeechRecognition(
  onResult: (transcript: string, isFinal: boolean, resultIndex: number) => void,
  /** BCP-47 tag the recognizer listens in, e.g. "en-US" or "fr-FR". */
  lang: string,
) {
  // Keep the latest callback without re-creating the recognition instance.
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  // Read at instance-creation time. The locale only changes via a full page
  // navigation, so the recognizer is always built with the right language.
  const langRef = useRef(lang);
  langRef.current = lang;

  const [supported] = useState(() => getCtor() !== null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recRef = useRef<SpeechRec | null>(null);
  const wantRef = useRef(false); // desired state, drives auto-restart
  const runningRef = useRef(false); // is the engine actually capturing now?
  const startingRef = useRef(false); // a start() is pending its onstart
  const watchdogRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Suspended while the app is reading aloud, so the mic can't hear the speaker
  // and mistake the app's own voice for a command. This is separate from
  // `wantRef` (the user's mic on/off): the user still WANTS to listen, we're
  // just briefly deaf while output plays. Driven by the mic gate; see below.
  const suspendedRef = useRef(false);
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Start the engine if we want it on and it isn't already running/starting or
  // suspended for output. Browsers throw if start() is called while active, so
  // we guard and swallow.
  const safeStart = useCallback(() => {
    const rec = recRef.current;
    if (
      !rec ||
      !wantRef.current ||
      suspendedRef.current ||
      runningRef.current ||
      startingRef.current
    ) {
      return;
    }
    startingRef.current = true;
    try {
      rec.start();
    } catch {
      // Already running, or a stale instance — clear the guard so the watchdog
      // can try again on its next tick.
      startingRef.current = false;
    }
  }, []);

  const stop = useCallback(() => {
    wantRef.current = false;
    if (watchdogRef.current !== null) {
      clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }
    if (resumeTimerRef.current !== null) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    suspendedRef.current = false; // clean slate for the next start()
    recRef.current?.abort();
    runningRef.current = false;
    startingRef.current = false;
    setListening(false);
  }, []);

  const start = useCallback(() => {
    const Ctor = getCtor();
    if (!Ctor) return;
    setError(null);

    let rec = recRef.current;
    if (!rec) {
      rec = new Ctor();
      rec.continuous = true;
      // Interim results let us react the moment a word is recognized instead of
      // waiting for the browser to detect end-of-speech (which felt slow).
      rec.interimResults = true;
      // We only ever read the top hypothesis ([0]); asking for a single
      // alternative avoids the engine computing/ranking extras, shaving latency.
      rec.maxAlternatives = 1;
      rec.lang = langRef.current;
      rec.onstart = () => {
        runningRef.current = true;
        startingRef.current = false;
        setListening(true);
      };
      rec.onaudiostart = () => {
        runningRef.current = true;
        startingRef.current = false;
      };
      rec.onresult = (e) => {
        // Backstop against the app hearing itself: while we're suspended for
        // read-aloud (or the browser is speaking right now), drop the result.
        // We already abort() the engine during output, but some platforms keep
        // delivering a buffered result for audio captured just before the abort
        // takes effect — this makes sure none of the app's own spoken words can
        // ever be acted on as a command, regardless of how abort() behaves.
        if (suspendedRef.current || isSpeaking()) return;
        const idx = e.results.length - 1;
        const last = e.results[idx];
        if (last && last[0]) {
          onResultRef.current(last[0].transcript, last.isFinal, idx);
        }
      };
      rec.onerror = (e) => {
        startingRef.current = false;
        if (e.error === "not-allowed" || e.error === "service-not-allowed") {
          setError("Microphone is blocked. Allow mic access to use voice.");
          wantRef.current = false;
          setListening(false);
        }
        // "no-speech"/"aborted"/"network" are transient — onend restarts us.
      };
      rec.onend = () => {
        runningRef.current = false;
        startingRef.current = false;
        if (!wantRef.current) {
          setListening(false);
          return;
        }
        // Suspended for read-aloud: stay stopped; the gate resumes us when the
        // app goes quiet. Keep `listening` true — the user still wants voice on.
        if (suspendedRef.current) return;
        // Browsers stop after a pause (or sometimes after each phrase); restart
        // immediately to minimize the "deaf" gap where speech would be missed.
        safeStart();
      };
      recRef.current = rec;
    }

    wantRef.current = true;
    setListening(true);
    safeStart();

    // Watchdog: some browsers stop recognition without ever firing `onend`
    // (a silent death), which would leave the mic looking on but deaf. Poll
    // and revive it whenever we want it on but it isn't running.
    if (watchdogRef.current === null) {
      watchdogRef.current = setInterval(() => {
        if (wantRef.current && !runningRef.current) safeStart();
      }, 1500);
    }
  }, [safeStart]);

  // While the app reads a hint aloud, suspend the engine so the mic can't hear
  // the speaker and mistake the app's own voice for a command; resume a short
  // tail after it falls silent. Driven by the mic gate, which SpeakProvider
  // drives from its speech-output events.
  useEffect(() => {
    return subscribeMicGate((suspended) => {
      if (suspended) {
        if (resumeTimerRef.current !== null) {
          clearTimeout(resumeTimerRef.current);
          resumeTimerRef.current = null;
        }
        suspendedRef.current = true;
        // Stop capturing but leave `wantRef` intact so `listening` stays on and
        // the watchdog/onend don't fight us (both skip a suspended engine).
        recRef.current?.abort();
        runningRef.current = false;
        startingRef.current = false;
      } else {
        if (resumeTimerRef.current !== null) clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = setTimeout(() => {
          resumeTimerRef.current = null;
          suspendedRef.current = false;
          if (wantRef.current) safeStart();
        }, RESUME_TAIL_MS);
      }
    });
  }, [safeStart]);

  // Stop listening when the app unmounts.
  useEffect(() => {
    return () => {
      wantRef.current = false;
      if (watchdogRef.current !== null) clearInterval(watchdogRef.current);
      if (resumeTimerRef.current !== null) clearTimeout(resumeTimerRef.current);
      recRef.current?.abort();
    };
  }, []);

  return { supported, listening, error, start, stop };
}
